import { z } from 'zod';

// Add proper type declaration for Vite's import.meta.env
interface ImportMetaEnv {
  [key: string]: string | undefined;
  // Add specific environment variables you know exist
  NEXT_PUBLIC_APP_HOST: string;
  NEXT_PUBLIC_API_HOST?: string;
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: string;
  NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN: string;
  NEXT_PUBLIC_QUICKBOOKS_ONLINE_URL: string;
  NEXT_PUBLIC_SUPABASE_ANON_KEY: string;
  NEXT_PUBLIC_SUPABASE_URL: string;
  NEXT_PUBLIC_TILLED_PUBLISHABLE_KEY: string;
  NEXT_PUBLIC_TILLED_SANDBOX_PUBLISHABLE_KEY: string;
  NEXT_PUBLIC_TRASHLAB_ENV: string;
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: string;
  NEXT_PUBLIC_RUTTER_PUBLIC_KEY?: string;
  NEXT_PUBLIC_RENDER_GIT_COMMIT: string;
  NEXT_PUBLIC_JABRA_KEY?: string;
  NEXT_PUBLIC_WEBSOCKET_URL: string;
  NEXT_PUBLIC_LIVEKIT_URL: string;
  NEXT_PUBLIC_SCENARIO_LAB?: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// Add type declaration for window.env
declare global {
  interface Window {
    env?: Record<string, string>;
  }
}

if (typeof process === 'undefined') {
  (window as any).process = {
    env: {
      ...import.meta.env,
      NODE_ENV: 'development',
    },
  };
}

const envSchema = z.object({
  NEXT_PUBLIC_APP_HOST: z.string(),
  NEXT_PUBLIC_API_HOST: z.string().optional(),
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string(),
  NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN: z.string(),
  NEXT_PUBLIC_QUICKBOOKS_ONLINE_URL: z.string(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
  NEXT_PUBLIC_SUPABASE_URL: z.string(),
  NEXT_PUBLIC_TILLED_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_TILLED_SANDBOX_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_TRASHLAB_ENV: z.string(),
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string(),
  NEXT_PUBLIC_RUTTER_PUBLIC_KEY: z.string().optional(),
  NEXT_PUBLIC_JABRA_KEY: z.string().optional(),
  NEXT_PUBLIC_E2E_TEST: z.string().optional(),
  NEXT_PUBLIC_WEBSOCKET_URL: z.string().optional(),
  NEXT_PUBLIC_LIVEKIT_URL: z.string().optional().default('ws://localhost:7880'),
  NEXT_PUBLIC_TL_WORKSPACE: z.string().optional(),
  NEXT_PUBLIC_SCENARIO_LAB: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
});

// Use window.env if available (runtime), otherwise fall back to build-time env
const envSource =
  (typeof window !== 'undefined' && window.env) ||
  import.meta.env ||
  process.env;

const env = envSchema.parse(envSource);

export default {
  ...env,
  isE2eTest: env.NEXT_PUBLIC_E2E_TEST === 'true',
  isProd: env.NEXT_PUBLIC_TRASHLAB_ENV === 'production',
  isLocal: env.NEXT_PUBLIC_TRASHLAB_ENV === 'local',
  isStaging: env.NEXT_PUBLIC_TRASHLAB_ENV === 'staging',
  isTest: env.NEXT_PUBLIC_TRASHLAB_ENV === 'test',
};
